import {
  desiDayRate,
  localDay,
  monthDays,
  orderFeeStepText,
  palletDayRate,
  trMoney,
  trMonth,
  trRate,
  type RateCard,
  type RateCardKind,
  type ServiceTariff,
  type TenantSummary,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { carrierLabel, decimalInput, fmt } from '../../format';
import type { TenantPricing } from '../../types';
import { ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { TariffTable } from '../../ui/tariff';

// The prices of one seller: the list price, or its own copy of a template (the list price or
// another seller's prices) edited here and versioned by its start date. The list price itself is
// edited with the same editor (ListPrice.tsx).

interface TierDraft {
  readonly minDesi: string;
  readonly maxDesi: string;
  readonly unitPrice: string;
}

interface CardDraft {
  readonly key: string;
  readonly kind: RateCardKind;
  readonly carrierCode: string | null;
  readonly name: string;
  readonly extraDesiPrice: string;
  readonly tiers: readonly TierDraft[];
}

/** A step of the graduated order fee as typed: its last order number ("" on the last, open step) and price. */
interface StepDraft {
  readonly upTo: string;
  readonly price: string;
}

type StorageUnit = 'm3_month' | 'desi_day';

interface TariffDraft {
  /** 'flat': orderFee for every order; 'graduated': by the order's number in the month (orderFeeTiers) */
  readonly orderFeeMode: 'flat' | 'graduated';
  readonly orderFee: string;
  readonly orderFeeTiers: readonly StepDraft[];
  readonly extraUnitFee: string;
  readonly receivingUnitFee: string;
  /** storage of shelf stock in the unit chosen: ₺ / m³ / ay (as the website) or ₺ / desi / gün (older tariffs) */
  readonly storageUnit: StorageUnit;
  readonly storageFee: string;
  readonly palletMonthFee: string;
  readonly boxFee: string;
  readonly defaultUnitDesi: string;
  readonly vatRate: string;
  readonly returnFee: string;
}

export interface PricingDraft {
  readonly validFrom: string;
  readonly cards: readonly CardDraft[];
  /** null: the list price's fixed fees */
  readonly tariff: TariffDraft | null;
}

export const KIND_LABEL: Record<RateCardKind, string> = { shipping: 'Kargo', handling: 'Elleçleme' };
type SimpleField = 'extraUnitFee' | 'palletMonthFee' | 'boxFee' | 'returnFee' | 'receivingUnitFee' | 'defaultUnitDesi' | 'vatRate';
const TARIFF_FIELDS: readonly { key: SimpleField; label: string; unit: string; digits: number }[] = [
  { key: 'extraUnitFee', label: 'Ek ürün', unit: '₺ / adet (siparişteki ilk üründen sonra)', digits: 2 },
  { key: 'palletMonthFee', label: 'Palet depolama', unit: '₺ / palet / ay', digits: 2 },
  { key: 'boxFee', label: 'Koli', unit: '₺ / koli (deponun kolisi; kendi ambalajıyla gidenden alınmaz)', digits: 2 },
  { key: 'returnFee', label: 'İade elleçleme', unit: '₺ / müşteri iadesi', digits: 2 },
  { key: 'receivingUnitFee', label: 'Mal kabul', unit: '₺ / adet (0: alınmaz)', digits: 4 },
  { key: 'defaultUnitDesi', label: 'Ölçülmemiş ürün', unit: 'desi / adet', digits: 2 },
  { key: 'vatRate', label: 'KDV', unit: '%', digits: 2 },
];
const LIST_TARIFF: TariffDraft = {
  orderFeeMode: 'flat',
  orderFee: '0',
  orderFeeTiers: [],
  extraUnitFee: '0',
  receivingUnitFee: '0',
  storageUnit: 'm3_month',
  storageFee: '0',
  palletMonthFee: '0',
  boxFee: '0',
  defaultUnitDesi: '1',
  vatRate: '20',
  returnFee: '0',
};

/** Today in Türkiye: price changes start from it (the server checks the same day) */
const today = () => localDay(new Date());
const plain = (v: string) => v.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
const typed = (v: string, digits = 2) => Number(decimalInput(v, digits) ?? Number.NaN);

/** The editor's copy of a tariff. */
export function tariffDraft(t: ServiceTariff): TariffDraft {
  const perMonth = t.storageM3MonthFee !== null || Number(t.storageDesiDayFee) === 0;
  return {
    orderFeeMode: t.orderFeeTiers.length > 0 ? 'graduated' : 'flat',
    orderFee: plain(t.orderFee),
    orderFeeTiers: t.orderFeeTiers.map((s) => ({ upTo: s.upTo === null ? '' : String(s.upTo), price: plain(s.price) })),
    extraUnitFee: plain(t.extraUnitFee),
    receivingUnitFee: plain(t.receivingUnitFee),
    storageUnit: perMonth ? 'm3_month' : 'desi_day',
    storageFee: plain(perMonth ? (t.storageM3MonthFee ?? '0') : t.storageDesiDayFee),
    palletMonthFee: plain(t.palletMonthFee),
    boxFee: plain(t.boxFee),
    defaultUnitDesi: plain(t.defaultUnitDesi),
    vatRate: plain(t.vatRate),
    returnFee: plain(t.returnFee ?? '0'),
  };
}

/** A draft from what a template seller pays (cards with tenantId null are the list price; no suffix: the list price itself). */
export function draftFrom(cards: readonly RateCard[], tariff: ServiceTariff | null, suffix: string): PricingDraft {
  return {
    validFrom: today(),
    cards: cards.map((c, i) => ({
      key: `${c.kind}|${c.carrierCode ?? ''}|${i}`,
      kind: c.kind,
      carrierCode: c.carrierCode,
      name: c.tenantId || !suffix ? c.name : `${c.name} · ${suffix}`,
      extraDesiPrice: c.extraDesiPrice ?? '',
      tiers: c.tiers.map((t) => ({ minDesi: String(t.minDesi), maxDesi: String(t.maxDesi), unitPrice: t.unitPrice })),
    })),
    tariff: tariff ? tariffDraft(tariff) : LIST_TARIFF,
  };
}

/** The request body of PUT /api/admin/tenants/:id/pricing and /api/admin/pricing/list, or the first problem found. */
export function pricingPayload(draft: PricingDraft): { body: unknown } | { problem: string } {
  const cards = [];
  for (const c of draft.cards) {
    const tiers = [];
    for (const [i, t] of c.tiers.entries()) {
      const min = Number(decimalInput(t.minDesi));
      const max = Number(decimalInput(t.maxDesi));
      const price = decimalInput(t.unitPrice);
      if (!Number.isFinite(min) || !Number.isFinite(max) || decimalInput(t.minDesi) === null || decimalInput(t.maxDesi) === null) {
        return { problem: `${c.name}: ${i + 1}. satırın desi aralığı sayı olmalı` };
      }
      if (price === null) return { problem: `${c.name}: ${i + 1}. satırın fiyatı sayı olmalı` };
      tiers.push({ minDesi: min, maxDesi: max, unitPrice: price });
    }
    if (tiers.length === 0) return { problem: `${c.name}: en az bir desi aralığı olmalı` };
    const extra = c.extraDesiPrice.trim() ? decimalInput(c.extraDesiPrice) : null;
    if (c.extraDesiPrice.trim() && extra === null) return { problem: `${c.name}: üst desi fiyatı sayı olmalı` };
    cards.push({ kind: c.kind, carrierCode: c.carrierCode, name: c.name.trim(), extraDesiPrice: extra, tiers });
  }
  let tariff = null;
  if (draft.tariff) {
    const t = draft.tariff;
    const values: Record<string, unknown> = {};
    for (const f of TARIFF_FIELDS) {
      const v = decimalInput(t[f.key], f.digits);
      if (v === null) return { problem: `${f.label} ücreti sayı olmalı` };
      values[f.key] = v;
    }
    if (t.orderFeeMode === 'graduated') {
      const steps = [];
      for (const [i, step] of t.orderFeeTiers.entries()) {
        const price = decimalInput(step.price);
        if (price === null) return { problem: `${i + 1}. kademenin fiyatı sayı olmalı` };
        const last = i === t.orderFeeTiers.length - 1;
        // "1.000" as typed in Turkish is a thousand
        const upTo = last ? null : Number(step.upTo.trim().replace(/[.\s]/g, ''));
        if (upTo !== null && (!Number.isInteger(upTo) || upTo < 1)) return { problem: `${i + 1}. kademenin son siparişi tam sayı olmalı` };
        steps.push({ upTo, price });
      }
      if (steps.length < 2) return { problem: 'Kademeli sipariş ücretinde en az iki kademe olmalı' };
      values.orderFeeTiers = steps;
      values.orderFee = steps[0]!.price;
    } else {
      const v = decimalInput(t.orderFee);
      if (v === null) return { problem: 'Sipariş başı ücreti sayı olmalı' };
      values.orderFee = v;
      values.orderFeeTiers = [];
    }
    const storage = decimalInput(t.storageFee, t.storageUnit === 'm3_month' ? 2 : 4);
    if (storage === null) return { problem: 'Depolama ücreti sayı olmalı' };
    values.storageM3MonthFee = t.storageUnit === 'm3_month' ? storage : null;
    values.storageDesiDayFee = t.storageUnit === 'm3_month' ? '0' : storage;
    tariff = values;
  }
  return { body: { validFrom: draft.validFrom, rateCards: cards, tariff } };
}

/** The graduated order fee: one price, or steps by the order's number in the seller's month. */
function OrderFeeEditor({ tariff, onChange }: { tariff: TariffDraft; onChange: (next: TariffDraft) => void }) {
  const steps = tariff.orderFeeTiers;
  const setStep = (index: number, next: Partial<StepDraft>) => onChange({ ...tariff, orderFeeTiers: steps.map((s, i) => (i === index ? { ...s, ...next } : s)) });
  const firstOf = (index: number) => (index === 0 ? 1 : (Number(steps[index - 1]!.upTo.replace(/[.\s]/g, '')) || 0) + 1);
  return (
    <div className="field wide">
      <label>Sipariş başı (toplama & paketleme)</label>
      <div className="row" style={{ gap: 16 }}>
        <label className="row" style={{ gap: 6 }}>
          <input type="radio" name="order-fee-mode" checked={tariff.orderFeeMode === 'flat'} onChange={() => onChange({ ...tariff, orderFeeMode: 'flat' })} />
          Tek fiyat
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="radio"
            name="order-fee-mode"
            checked={tariff.orderFeeMode === 'graduated'}
            onChange={() =>
              onChange({
                ...tariff,
                orderFeeMode: 'graduated',
                orderFeeTiers: steps.length >= 2 ? steps : [{ upTo: '250', price: tariff.orderFee }, { upTo: '', price: tariff.orderFee }],
              })
            }
          />
          Kademeli (ayın kaçıncı siparişi)
        </label>
      </div>
      {tariff.orderFeeMode === 'flat' ? (
        <>
          <input className="input" style={{ maxWidth: 180 }} inputMode="decimal" value={tariff.orderFee} onChange={(e) => onChange({ ...tariff, orderFee: e.target.value })} aria-label="Sipariş başı ücret" />
          <span className="hint">₺ / paketlenen sipariş</span>
        </>
      ) : (
        <>
          <table className="t tiers" style={{ maxWidth: 520 }}>
            <thead>
              <tr>
                <th>Ayın siparişleri</th>
                <th className="num">Fiyat (₺ / sipariş)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {steps.map((step, i) => {
                const last = i === steps.length - 1;
                return (
                  <tr key={i}>
                    <td>
                      {last ? (
                        orderFeeStepText(firstOf(i), null)
                      ) : (
                        <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                          {firstOf(i)} –
                          <input className="input" style={{ width: 110 }} inputMode="numeric" value={step.upTo} onChange={(e) => setStep(i, { upTo: e.target.value })} aria-label={`${i + 1}. kademenin son siparişi`} />
                          . sipariş
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <input className="input num" inputMode="decimal" value={step.price} onChange={(e) => setStep(i, { price: e.target.value })} aria-label={`${i + 1}. kademenin fiyatı`} />
                    </td>
                    <td>
                      {!last && steps.length > 2 && (
                        <button className="icon-btn" onClick={() => onChange({ ...tariff, orderFeeTiers: steps.filter((_, j) => j !== i) })} aria-label="Kademeyi sil">
                          <Icon name="x" size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="row">
            <button
              className="btn sm"
              disabled={steps.length >= 10}
              onClick={() => {
                // a new step before the open one, 250 orders after the last bound
                const before = steps.slice(0, -1);
                const bound = (Number(before.at(-1)?.upTo.replace(/[.\s]/g, '')) || 0) + 250;
                onChange({ ...tariff, orderFeeTiers: [...before, { upTo: String(bound), price: steps.at(-1)!.price }, steps.at(-1)!] });
              }}
            >
              <Icon name="plus" size={16} /> Kademe ekle
            </button>
          </div>
          <span className="hint">
            Satıcının bir ayda (Türkiye saatiyle) paketlenen siparişleri sırayla sayılır; her sipariş düştüğü kademenin fiyatıyla ücretlenir (sitedeki “kademeli tarife”). Ay başında sayım yeniden başlar.
          </span>
        </>
      )}
    </div>
  );
}

/** Shelf storage in ₺ / m³ / ay (the website's unit) or ₺ / desi / gün, with what a day costs. */
function StorageEditor({ tariff, onChange }: { tariff: TariffDraft; onChange: (next: TariffDraft) => void }) {
  const day = today();
  const days = monthDays(day);
  const value = typed(tariff.storageFee, tariff.storageUnit === 'm3_month' ? 2 : 4);
  const switchTo = (unit: StorageUnit) => {
    if (unit === tariff.storageUnit) return;
    // the same price in the other unit, over a 30-day month: ₺ / m³ / ay = ₺ / desi / gün × 333.33 × 30
    const converted = Number.isFinite(value) ? (unit === 'desi_day' ? (value / 10_000).toFixed(4) : (value * 10_000).toFixed(2)) : '0';
    onChange({ ...tariff, storageUnit: unit, storageFee: plain(converted) });
  };
  return (
    <div className="field">
      <label>{Number(typed(tariff.palletMonthFee)) > 0 ? 'Depolama (raf / koli)' : 'Depolama'}</label>
      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
        <input className="input" inputMode="decimal" value={tariff.storageFee} onChange={(e) => onChange({ ...tariff, storageFee: e.target.value })} aria-label="Depolama ücreti" />
        <select className="select" style={{ width: 'auto' }} value={tariff.storageUnit} onChange={(e) => switchTo(e.target.value as StorageUnit)} aria-label="Depolama birimi">
          <option value="m3_month">₺ / m³ / ay</option>
          <option value="desi_day">₺ / desi / gün</option>
        </select>
      </div>
      <span className="hint">
        {!Number.isFinite(value)
          ? 'Sayı girin'
          : tariff.storageUnit === 'm3_month'
            ? `Her gün günün sonundaki stokla işlenir: ÷ 333,33 desi ÷ ayın gün sayısı. ${trMonth(day)} (${days} gün): ${trRate(desiDayRate(String(value), days))} ₺ / desi / gün. Kuruş farkı ay içinde birikmez: bütün ay duran 1 m³ (333,33 desi) = ${trMoney(value)} ₺.`
            : `Her gün günün sonundaki stokla işlenir. 30 günlük ayda 1 m³ (333,33 desi) = ${trMoney(value * 10_000)} ₺.`}
      </span>
    </div>
  );
}

function palletHint(fee: string): string {
  const value = typed(fee);
  if (!Number.isFinite(value)) return 'Sayı girin';
  if (value === 0) return 'Ayrı palet fiyatı yok: palet gözündeki stok da desiyle ücretlenir.';
  const day = today();
  const days = monthDays(day);
  return `Günün sonunda satıcının ürünü bulunan her palet gözü bir palet: ÷ ayın gün sayısı. ${trMonth(day)} (${days} gün): ${trRate(palletDayRate(String(value), days))} ₺ / palet / gün; bütün ay duran bir palet = ${trMoney(value)} ₺. Birkaç satıcının ortak palet gözü, ürünlerin kapladığı yere göre paylaştırılır (palet bir kez ücretlenir). Palet gözündeki stoktan desi ücreti alınmaz.`;
}

/** The desi per unmeasured unit: with storage per m³ it applies only where the bin does not decide. */
function unmeasuredHint(storageUnit: StorageUnit): string {
  return storageUnit === 'm3_month'
    ? 'desi / adet: yalnız hacmi tanımlı bir raf ya da palet gözünde olmayan (ya da gözü ölçülü ürünlerle dolu görünen) ölçüsüz ürünlerde. Hacmi tanımlı gözdeki ölçüsüz ürün, ölçülü ürünlerin bıraktığı yeri o gözün ölçüsüz ürünleriyle adet oranında paylaşır.'
    : 'desi / adet: ölçüsü girilmemiş ürünün depolamada sayılan desisi.';
}

/** Carriers a shipping barem can be for (none: every carrier). */
const CARD_CARRIERS = ['aras', 'yurtici', 'mng', 'surat', 'ptt', 'hepsijet', 'kolay_gelsin', 'sendeo', 'ups'] as const;

/** Adds a barem the price list does not have yet: elleçleme, or kargo for one carrier or all. */
function AddCard({ draft, onChange }: { draft: PricingDraft; onChange: (next: PricingDraft) => void }) {
  const [choice, setChoice] = useState('handling|');
  const taken = new Set(draft.cards.map((c) => `${c.kind}|${c.carrierCode ?? ''}`));
  const options: { value: string; label: string }[] = [
    { value: 'handling|', label: 'Elleçleme baremi (desiye göre, koli başı)' },
    { value: 'shipping|', label: 'Kargo baremi · tüm kargo firmaları' },
    ...CARD_CARRIERS.map((c) => ({ value: `shipping|${c}`, label: `Kargo baremi · ${carrierLabel(c)}` })),
  ].filter((o) => !taken.has(o.value));
  if (options.length === 0) return null;
  const value = options.some((o) => o.value === choice) ? choice : options[0]!.value;
  return (
    <div className="row">
      <select className="select" style={{ width: 'auto' }} value={value} onChange={(e) => setChoice(e.target.value)} aria-label="Eklenecek barem">
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        className="btn sm"
        onClick={() => {
          const [kind, carrier] = value.split('|') as [RateCardKind, string];
          const name = kind === 'handling' ? 'Elleçleme baremi' : `Kargo baremi · ${carrierLabel(carrier || null)}`;
          onChange({
            ...draft,
            cards: [...draft.cards, { key: `${value}|${Date.now()}`, kind, carrierCode: carrier || null, name, extraDesiPrice: '', tiers: [{ minDesi: '0', maxDesi: '1', unitPrice: '0' }] }],
          });
        }}
      >
        <Icon name="plus" size={16} /> Barem ekle
      </button>
    </div>
  );
}

/**
 * Editable copy of a price list: barem rows per card and the fixed fees. `list`: the list price
 * itself (nothing to fall back to), otherwise a seller's own prices over the list price.
 */
export function PricingEditor({ draft, onChange, list = false }: { draft: PricingDraft; onChange: (next: PricingDraft) => void; list?: boolean }) {
  const setCard = (key: string, next: Partial<CardDraft>) => onChange({ ...draft, cards: draft.cards.map((c) => (c.key === key ? { ...c, ...next } : c)) });
  const setTier = (card: CardDraft, index: number, next: Partial<TierDraft>) =>
    setCard(card.key, { tiers: card.tiers.map((t, i) => (i === index ? { ...t, ...next } : i === index + 1 && next.maxDesi !== undefined ? { ...t, minDesi: next.maxDesi } : t)) });

  return (
    <div className="stack">
      <div className="field" style={{ maxWidth: 240 }}>
        <label htmlFor="price-from">Geçerlilik başlangıcı</label>
        <input id="price-from" className="input" type="date" min={today()} value={draft.validFrom} onChange={(e) => onChange({ ...draft, validFrom: e.target.value })} />
      </div>
      {draft.cards.length === 0 && (
        <p className="muted">{list ? 'Barem yok: elleçlemede desi baremi uygulanmaz, kargo liste fiyatında yok.' : 'Kargo ve elleçleme baremlerinde liste fiyatı uygulanır.'}</p>
      )}
      {draft.cards.map((card) => (
        <div key={card.key} className="price-card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="row" style={{ gap: 8 }}>
              <span className="chip info">{KIND_LABEL[card.kind]}</span>
              {card.kind === 'shipping' && <b>{carrierLabel(card.carrierCode)}</b>}
            </div>
            <button
              className="btn sm ghost"
              onClick={() => onChange({ ...draft, cards: draft.cards.filter((c) => c.key !== card.key) })}
              title={list ? 'Bu barem liste fiyatından çıksın' : 'Bu baremde liste fiyatı uygulansın'}
            >
              <Icon name="trash" size={16} /> {list ? 'Baremi kaldır' : 'Liste fiyatı kullan'}
            </button>
          </div>
          <div className="field">
            <label>Barem adı</label>
            <input className="input" value={card.name} onChange={(e) => setCard(card.key, { name: e.target.value })} />
          </div>
          <table className="t tiers">
            <thead>
              <tr>
                <th>Desi (üstü)</th>
                <th>Desi (dahil)</th>
                <th className="num">Fiyat (₺)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {card.tiers.map((t, i) => (
                <tr key={i}>
                  <td>
                    <input className="input" inputMode="decimal" value={t.minDesi} disabled={i > 0} onChange={(e) => setTier(card, i, { minDesi: e.target.value })} aria-label="Desi alt sınırı" />
                  </td>
                  <td>
                    <input className="input" inputMode="decimal" value={t.maxDesi} onChange={(e) => setTier(card, i, { maxDesi: e.target.value })} aria-label="Desi üst sınırı" />
                  </td>
                  <td className="num">
                    <input className="input num" inputMode="decimal" value={t.unitPrice} onChange={(e) => setTier(card, i, { unitPrice: e.target.value })} aria-label="Fiyat" />
                  </td>
                  <td>
                    {card.tiers.length > 1 && i === card.tiers.length - 1 && (
                      <button className="icon-btn" onClick={() => setCard(card.key, { tiers: card.tiers.slice(0, -1) })} aria-label="Satırı sil">
                        <Icon name="x" size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row">
            <button
              className="btn sm"
              onClick={() => {
                const last = card.tiers[card.tiers.length - 1];
                const from = last ? last.maxDesi : '0';
                setCard(card.key, { tiers: [...card.tiers, { minDesi: from, maxDesi: String((Number(decimalInput(from)) || 0) + 5), unitPrice: last?.unitPrice ?? '0' }] });
              }}
            >
              <Icon name="plus" size={16} /> Desi aralığı ekle
            </button>
            <label className="row" style={{ gap: 6 }}>
              <span className="muted">Son aralıktan sonra her desi</span>
              <input className="input" style={{ width: 110 }} inputMode="decimal" placeholder="yok" value={card.extraDesiPrice} onChange={(e) => setCard(card.key, { extraDesiPrice: e.target.value })} />
              <span className="muted">₺</span>
            </label>
          </div>
        </div>
      ))}

      <AddCard draft={draft} onChange={onChange} />

      <div className="price-card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <b>Sabit ücretler</b>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={draft.tariff === null} onChange={(e) => onChange({ ...draft, tariff: e.target.checked ? null : LIST_TARIFF })} />
            {list ? 'Sabit ücret yok' : 'Liste fiyatını kullan'}
          </label>
        </div>
        {list && draft.tariff === null && <p className="muted" style={{ margin: 0 }}>Liste fiyatındaki satıcılardan sipariş, depolama, koli, iade ve mal kabul ücreti alınmaz.</p>}
        {draft.tariff && (
          <div className="form-grid">
            <OrderFeeEditor tariff={draft.tariff} onChange={(tariff) => onChange({ ...draft, tariff })} />
            <StorageEditor tariff={draft.tariff} onChange={(tariff) => onChange({ ...draft, tariff })} />
            {TARIFF_FIELDS.map((f) => (
              <div key={f.key} className="field">
                <label>{f.label}</label>
                <input className="input" inputMode="decimal" aria-label={f.label} value={draft.tariff![f.key]} onChange={(e) => onChange({ ...draft, tariff: { ...draft.tariff!, [f.key]: e.target.value } })} />
                <span className="hint">
                  {f.key === 'palletMonthFee' ? `${f.unit}. ${palletHint(draft.tariff![f.key])}` : f.key === 'defaultUnitDesi' ? unmeasuredHint(draft.tariff!.storageUnit) : f.unit}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Picks the template a new own price list starts from: the list price or another seller's prices. */
export function TemplatePicker(props: { tenants: readonly TenantSummary[]; exclude?: string; value: string; onChange: (value: string) => void }) {
  const sources = props.tenants.filter((t) => t.customPricing && t.id !== props.exclude);
  return (
    <div className="field" style={{ maxWidth: 360 }}>
      <label htmlFor="price-template">Şablon</label>
      <select id="price-template" className="select" value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        <option value="">Liste fiyatı (standart)</option>
        {sources.map((t) => (
          <option key={t.id} value={t.id}>
            {t.code} · {t.displayName} müşterisinin fiyatları
          </option>
        ))}
      </select>
    </div>
  );
}

/** Loads a template as a draft: the list price (cards and tariff without a seller) or a seller's pricing. */
export async function loadTemplate(templateTenantId: string, suffix: string): Promise<PricingDraft> {
  if (templateTenantId) {
    const p = await api<TenantPricing>('GET', `/api/admin/tenants/${templateTenantId}/pricing`);
    return draftFrom(p.rateCards, p.tariff, suffix);
  }
  const [cards, tariffs] = await Promise.all([api<RateCard[]>('GET', '/api/billing/rate-cards'), api<ServiceTariff[]>('GET', '/api/billing/tariffs')]);
  return draftFrom(
    cards.filter((c) => c.tenantId === null),
    tariffs.find((t) => t.tenantId === null) ?? null,
    suffix,
  );
}

export function TierTable({ card }: { card: RateCard }) {
  return (
    <table className="t tiers compact">
      <tbody>
        {card.tiers.map((t) => (
          <tr key={t.minDesi}>
            <td>
              {fmt.dec(t.minDesi)} – {fmt.dec(t.maxDesi)} desi
            </td>
            <td className="num">{fmt.money(t.unitPrice)}</td>
          </tr>
        ))}
        {card.extraDesiPrice && (
          <tr>
            <td>Üstü, her desi</td>
            <td className="num">{fmt.money(card.extraDesiPrice)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** The seller's prices today, where each comes from, and the admin's ways to change them. */
export function PricingPanel({ tenant, tenants, canEdit }: { tenant: TenantSummary; tenants: readonly TenantSummary[]; canEdit: boolean }) {
  const toast = useToast();
  const pricing = useLoad(() => api<TenantPricing>('GET', `/api/admin/tenants/${tenant.id}/pricing`), [tenant.id]);
  const [editing, setEditing] = useState<PricingDraft | null>(null);
  const [template, setTemplate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = async (from: 'current' | string) => {
    setError(null);
    try {
      if (from === 'current' && pricing.data) setEditing(draftFrom(pricing.data.rateCards, pricing.data.tariff, tenant.code));
      else setEditing(await loadTemplate(from === 'current' ? '' : from, tenant.code));
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const save = async () => {
    if (!editing) return;
    const payload = pricingPayload(editing);
    if ('problem' in payload) return setError(payload.problem);
    setBusy(true);
    try {
      await api('PUT', `/api/admin/tenants/${tenant.id}/pricing`, payload.body);
      toast(`${tenant.displayName} için özel fiyatlar ${fmt.date(editing.validFrom)} itibarıyla geçerli`, 'ok');
      setEditing(null);
      void pricing.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const backToList = async () => {
    if (!window.confirm(`${tenant.displayName} bugünden itibaren liste fiyatına dönsün mü?`)) return;
    try {
      await api('DELETE', `/api/admin/tenants/${tenant.id}/pricing`);
      toast('Liste fiyatına dönüldü', 'ok');
      void pricing.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  if (pricing.error) return <ErrorBanner message={pricing.error} onRetry={() => void pricing.reload()} />;
  if (!pricing.data) return <Spinner />;
  const p = pricing.data;
  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <span className={`chip ${p.custom ? 'info' : ''}`}>{p.custom ? 'Müşteriye özel fiyat' : 'Liste fiyatı'}</span>
          {p.upcomingChanges > 0 && <span className="chip warn" style={{ marginLeft: 6 }}>İleri tarihli değişiklik var</span>}
        </div>
        {canEdit && (
          <div className="row">
            {!p.custom && <TemplatePicker tenants={tenants} exclude={tenant.id} value={template} onChange={setTemplate} />}
            <button className="btn primary" onClick={() => void open(p.custom ? 'current' : template || 'current')}>
              <Icon name="edit" size={16} /> {p.custom ? 'Özel fiyatı düzenle' : 'Özel fiyat tanımla'}
            </button>
            {p.custom && (
              <button className="btn" onClick={() => void backToList()}>
                Liste fiyatına dön
              </button>
            )}
          </div>
        )}
      </div>
      <div className="grid">
        {p.rateCards.map((c) => (
          <div key={c.id} className="price-card">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="chip">{KIND_LABEL[c.kind]}</span>
              <span className={`chip ${c.tenantId ? 'info' : ''}`}>{c.tenantId ? 'Özel' : 'Liste'}</span>
            </div>
            <b>{c.kind === 'shipping' ? carrierLabel(c.carrierCode) : 'Paket başı elleçleme'}</b>
            <span className="muted" style={{ fontSize: 13 }}>
              {c.name} · {fmt.date(c.validFrom)} itibarıyla
            </span>
            <TierTable card={c} />
          </div>
        ))}
        {p.tariff && (
          <div className="price-card wide">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="chip">Sabit ücretler</span>
              <span className={`chip ${p.tariff.tenantId ? 'info' : ''}`}>{p.tariff.tenantId ? 'Özel' : 'Liste'}</span>
            </div>
            <div className="table-wrap">
              <TariffTable tariff={p.tariff} compact />
            </div>
          </div>
        )}
      </div>

      {editing && (
        <Modal
          wide
          title={`${tenant.displayName} · özel fiyatlar`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button className="btn" onClick={() => setEditing(null)}>
                Vazgeç
              </button>
              <button className="btn primary" disabled={busy} onClick={() => void save()}>
                Kaydet
              </button>
            </>
          }
        >
          {error && <div className="banner bad" style={{ marginBottom: 12 }}>{error}</div>}
          {p.upcomingChanges > 0 && (
            <div className="banner warn" style={{ marginBottom: 12 }}>
              İleri tarihli özel fiyat sürümü var: seçtiğiniz günde ya da sonrasında başlayan sürümler silinir, yerine bu fiyat süresiz geçerli olur.
            </div>
          )}
          <PricingEditor draft={editing} onChange={setEditing} />
        </Modal>
      )}
    </div>
  );
}
